"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import {
  BookOpen,
  CalendarDays,
  Home,
  Menu,
  ShoppingBag,
  Users,
  X,
} from "lucide-react";
import { IconButton, NavItem } from "@nitro/ui";
import { HeaderAuthButton } from "./HeaderAuthButton";
import { LanguageTranslator } from "./LanguageTranslator";
import { ShopCartButton } from "./ShopCartButton";
import { PUBLIC_STAGE_LINKS } from "../lib/publicStage";

const navIcons = { Home, CalendarDays, BookOpen, Users, ShoppingBag };
const publicNav = PUBLIC_STAGE_LINKS.map((item) => ({ ...item, icon: navIcons[item.icon] }));

export function PublicNavigation({ logoUrl }) {
  const pathname = usePathname();
  const [compactNavigation, setCompactNavigation] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const navigationWasOpen = useRef(false);
  const drawerRef = useRef(null);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 980px)");
    const syncNavigationMode = () => setCompactNavigation(media.matches);
    syncNavigationMode();
    media.addEventListener("change", syncNavigationMode);
    return () => media.removeEventListener("change", syncNavigationMode);
  }, []);

  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!compactNavigation) {
      navigationWasOpen.current = navOpen;
      return;
    }
    if (navOpen) {
      document.querySelector(".nitro-public-nav-close")?.focus();
    } else if (navigationWasOpen.current) {
      document.querySelector(".nitro-public-nav-toggle")?.focus();
    }
    navigationWasOpen.current = navOpen;
  }, [compactNavigation, navOpen]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setNavOpen(false);
      if (event.key !== "Tab" || !compactNavigation) return;
      const controls = Array.from(drawerRef.current?.querySelectorAll('a[href], button:not([disabled])') || [])
        .filter((element) => element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) return;
      if (!drawerRef.current?.contains(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [compactNavigation, navOpen]);

  useEffect(() => {
    if (!compactNavigation || !navOpen) return undefined;
    const backgroundTargets = [
      document.getElementById("nitro-public-content"),
      document.querySelector(".sim-brand-bar"),
      document.querySelector(".nitro-ui-skip-link"),
    ].filter(Boolean);

    backgroundTargets.forEach((element) => {
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
    });
    document.documentElement.classList.add("nitro-public-nav-open");

    return () => {
      backgroundTargets.forEach((element) => {
        element.inert = false;
        element.removeAttribute("aria-hidden");
      });
      document.documentElement.classList.remove("nitro-public-nav-open");
    };
  }, [compactNavigation, navOpen]);

  return (
    <header className="nitro-header nitro-ui-density-editorial nitro-ui-contract-v2 nitro-ui-topbar">
      <a
        aria-hidden={compactNavigation && navOpen ? "true" : undefined}
        className="nitro-brand"
        href="/"
        inert={compactNavigation && navOpen ? true : undefined}
        aria-label="Nitro Simracing inicio"
      >
        <img src={logoUrl} alt="Nitro Simracing Club" />
      </a>

      <IconButton
        aria-hidden={compactNavigation && navOpen ? "true" : undefined}
        inert={compactNavigation && navOpen ? true : undefined}
        aria-controls="nitro-public-navigation"
        aria-expanded={navOpen}
        className="nitro-public-nav-toggle"
        icon={<Menu size={20} />}
        label="Abrir navegación"
        onClick={() => setNavOpen(true)}
        variant="secondary"
      />

      <aside
        aria-label={compactNavigation ? "Navegación pública" : undefined}
        aria-modal={compactNavigation && navOpen ? "true" : undefined}
        aria-hidden={compactNavigation && !navOpen ? "true" : undefined}
        className={`nitro-public-nav-drawer${navOpen ? " is-open" : ""}`}
        id="nitro-public-navigation"
        inert={compactNavigation && !navOpen ? true : undefined}
        ref={drawerRef}
        role={compactNavigation ? "dialog" : undefined}
      >
        <div className="nitro-public-nav-heading">
          <div>
            <span>Nitro Simracing</span>
            <strong>Explorar</strong>
          </div>
          <IconButton
            className="nitro-public-nav-close"
            icon={<X size={19} />}
            label="Cerrar navegación"
            onClick={() => setNavOpen(false)}
            variant="ghost"
          />
        </div>

        <nav className="nitro-nav" aria-label="Navegacion publica">
          {publicNav.map((item) => {
            const Icon = item.icon;
            const active = isActiveNavItem(pathname, item.href);
            return (
              <NavItem
                active={active}
                href={item.href}
                icon={<Icon size={18} />}
                key={item.href}
                onClick={() => setNavOpen(false)}
                placement={compactNavigation ? "sidebar" : "topbar"}
              >
                {item.label}
              </NavItem>
            );
          })}
        </nav>
      </aside>

      <button
        aria-hidden="true"
        className={`nitro-public-nav-backdrop${navOpen ? " is-open" : ""}`}
        onClick={() => setNavOpen(false)}
        tabIndex={-1}
        type="button"
      />

      <div
        aria-hidden={compactNavigation && navOpen ? "true" : undefined}
        className="nitro-header-actions"
        inert={compactNavigation && navOpen ? true : undefined}
      >
        <LanguageTranslator />
        <HeaderAuthButton />
        <ShopCartButton />
      </div>
    </header>
  );
}

function isActiveNavItem(pathname, href) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}
